import {ref, shallowRef} from 'vue'

// Module-level singleton (same pattern as `useMessageSelection.ts`/`useMessageReply.ts`) backing a
// single, app-wide `<EmojiPicker>` instance — see `SharedEmojiPickerHost.vue`.
//
// Why this exists: `emoji-mart-vue-fast`'s `Picker` is not virtualized (`Picker.vue` renders every
// category up front and only toggles them with `v-show`), so one instance means ~1800 `<button>`s
// plus as many `EmojiView` objects built in a single synchronous pass. Measured on device that is
// ~900ms of JS plus ~550ms of style recalc/layout/paint. Reka's `Popover` unmounts its content on
// close, so that whole cost used to be paid *on every open*, of every picker, and the tap that
// opened it froze the UI for well over a second.
//
// So the picker is built once and never unmounted: it lives in a hidden stash element and is
// teleported into whichever popover currently wants it. Moving DOM through `<Teleport>` doesn't
// re-create the component, so opening only costs the layout/paint of showing existing nodes.
//
// A single shared instance rather than one-per-popover is deliberate: `EmojiReactionPopover` is
// rendered per message (up to three times each), and keeping a picker alive for every message
// that was ever opened would leak the node count linearly across a session.

// The element the picker should currently be teleported into — the open popover's host div, or
// `null` when nothing has claimed it (then `SharedEmojiPickerHost` parks it back in its stash).
const hostEl = shallowRef<HTMLElement | null>(null)
// Where the current claimer wants selected emoji delivered. Bound together with `hostEl` so a
// selection can never be routed to a popover that no longer owns the picker.
const selectHandler = shallowRef<((native: string) => void) | null>(null)
// Flipped once and never back: whether the picker component has been created at all. Set by the
// host's idle prewarm, or eagerly by the first claimer if it beats the prewarm to it.
const isBuilt = ref(false)

export function useSharedEmojiPicker() {
  function build() {
    isBuilt.value = true
  }

  /** Moves the picker into `el` and routes its selections to `onSelect`. */
  function claim(el: HTMLElement, onSelect: (native: string) => void) {
    isBuilt.value = true
    hostEl.value = el
    selectHandler.value = onSelect
  }

  /** No-op unless `el` is still the current host — guards against a popover that closed *after*
   *  another one already claimed the picker yanking it out from under the new owner. */
  function release(el: HTMLElement | null) {
    if (!el || hostEl.value !== el) return
    hostEl.value = null
    selectHandler.value = null
  }

  function emitSelect(native: string) {
    selectHandler.value?.(native)
  }

  return {hostEl, isBuilt, build, claim, release, emitSelect}
}
